import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api';

export interface AddressValues {
  address_street:  string;
  address_line2:   string;
  address_city:    string;
  address_state:   string;
  address_postal:  string;
  address_country: string;
}

interface AddressFieldsProps {
  values:    AddressValues;
  onChange:  (patch: Partial<AddressValues>) => void;
  /** Called after validation completes */
  onValidated?: (deliverability: 'deliverable' | 'undeliverable' | 'unknown') => void;
  disabled?: boolean;
}

type ValidationStatus = 'idle' | 'checking' | 'deliverable' | 'undeliverable' | 'unknown';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC',
];

const COUNTRIES = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'AU', label: 'Australia' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'MX', label: 'Mexico' },
  { code: 'JP', label: 'Japan' },
  { code: 'BR', label: 'Brazil' },
  { code: 'IN', label: 'India' },
];

export default function AddressFields({
  values,
  onChange,
  onValidated,
  disabled = false,
}: AddressFieldsProps) {
  const [status, setStatus] = useState<ValidationStatus>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Validate whenever the address fields reach a "complete enough" state
  useEffect(() => {
    const { address_street, address_city, address_country } = values;
    if (!address_street.trim() || !address_city.trim()) {
      setStatus('idle');
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runValidation();
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.address_street,
    values.address_city,
    values.address_state,
    values.address_postal,
    values.address_country,
  ]);

  async function runValidation() {
    setStatus('checking');

    // Hard 6-second timeout — if Lob is slow, fall back to 'unknown' so
    // the user can still save without being stuck forever.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);

    try {
      const json = await apiFetch<{ deliverability: 'deliverable' | 'undeliverable' | 'unknown' }>(
        '/api/contacts/validate-address',
        {
          method: 'POST',
          body: JSON.stringify({
            street:  values.address_street,
            line2:   values.address_line2 || undefined,
            city:    values.address_city,
            state:   values.address_state,
            postal:  values.address_postal,
            country: values.address_country,
          }),
          signal: controller.signal,
        },
      );
      setStatus(json.deliverability);
      onValidated?.(json.deliverability);
    } catch {
      // Timeout or network error — allow saving with unknown status
      setStatus('unknown');
      onValidated?.('unknown');
    } finally {
      clearTimeout(timeout);
    }
  }

  const isUS = values.address_country === 'US';

  return (
    <div className="flex flex-col gap-3">

      {/* Country */}
      <div>
        <label className="block font-body text-xs font-medium text-gray-600 mb-1">
          Country
        </label>
        <select
          value={values.address_country}
          onChange={(e) => onChange({ address_country: e.target.value })}
          disabled={disabled}
          className="input-field w-full"
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
          <option value="other">Other</option>
        </select>
      </div>

      {/* Street */}
      <div>
        <label className="block font-body text-xs font-medium text-gray-600 mb-1">
          Street address
        </label>
        <input
          type="text"
          placeholder="123 Main St"
          value={values.address_street}
          onChange={(e) => onChange({ address_street: e.target.value })}
          disabled={disabled}
          className="input-field w-full"
          autoComplete="street-address"
        />
      </div>

      {/* Apt / Unit */}
      <div>
        <label className="block font-body text-xs font-medium text-gray-600 mb-1">
          Apt, suite, or unit <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          placeholder="Apt 4B, Unit 12, Suite 200…"
          value={values.address_line2}
          onChange={(e) => onChange({ address_line2: e.target.value })}
          disabled={disabled}
          className="input-field w-full"
          autoComplete="address-line2"
        />
      </div>

      {/* City + State + ZIP row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="col-span-2 sm:col-span-1">
          <label className="block font-body text-xs font-medium text-gray-600 mb-1">
            City
          </label>
          <input
            type="text"
            placeholder="Charlotte"
            value={values.address_city}
            onChange={(e) => onChange({ address_city: e.target.value })}
            disabled={disabled}
            className="input-field w-full"
            autoComplete="address-level2"
          />
        </div>

        <div>
          <label className="block font-body text-xs font-medium text-gray-600 mb-1">
            {isUS ? 'State' : 'Province'}
          </label>
          {isUS ? (
            <select
              value={values.address_state}
              onChange={(e) => onChange({ address_state: e.target.value })}
              disabled={disabled}
              className="input-field w-full"
            >
              <option value="">,</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder="Ontario"
              value={values.address_state}
              onChange={(e) => onChange({ address_state: e.target.value })}
              disabled={disabled}
              className="input-field w-full"
              autoComplete="address-level1"
            />
          )}
        </div>

        <div>
          <label className="block font-body text-xs font-medium text-gray-600 mb-1">
            {isUS ? 'ZIP code' : 'Postal code'}
          </label>
          <input
            type="text"
            placeholder={isUS ? '28202' : 'M5V 2T6'}
            value={values.address_postal}
            onChange={(e) => onChange({ address_postal: e.target.value })}
            disabled={disabled}
            className="input-field w-full"
            autoComplete="postal-code"
          />
        </div>
      </div>

      {/* Validation status */}
      {status !== 'idle' && (
        <div className={`flex items-center gap-2 text-xs font-body rounded-lg px-3 py-2 ${
          status === 'checking'      ? 'bg-gray-50 text-gray-400' :
          status === 'deliverable'   ? 'bg-sage/10 text-sage' :
          status === 'undeliverable' ? 'bg-rose/20 text-rose-dark' :
          /* unknown */                'bg-gold/10 text-gold-dark'
        }`}
          style={status === 'undeliverable' ? { color: '#c0534a' } :
                 status === 'unknown'        ? { color: '#9A7B2C' } : {}}
        >
          {status === 'checking' && (
            <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
          )}
          {status === 'deliverable' && (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
          {status === 'undeliverable' && (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {status === 'unknown' && (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M12 3a9 9 0 1 0 0 18A9 9 0 0 0 12 3z" />
            </svg>
          )}
          <span>
            {status === 'checking'      && 'Checking address…'}
            {status === 'deliverable'   && 'Address verified, we can deliver here'}
            {status === 'undeliverable' && 'Address may not be deliverable, double-check it'}
            {status === 'unknown'       && "Couldn't verify, you can still save"}
          </span>
        </div>
      )}
    </div>
  );
}
